import React, { useEffect, useMemo, useRef, useState } from "react";
import { UserRound } from "lucide-react";
import { apiListTravellers } from "@/api";
import { useAuth } from "@/context/AuthContext";

let cache = null;
export const refreshSavedTravellers = () => { cache = null; };

export const useSavedTravellers = () => {
  const { user } = useAuth();
  const [list, setList] = useState(cache || []);
  useEffect(() => {
    if (!user) { setList([]); return; }
    if (cache) { setList(cache); return; }
    apiListTravellers().then((d) => { cache = d.travellers || []; setList(cache); }).catch(() => {});
  }, [user]);
  return list;
};

// Saved-customer contacts (email + mobile) for agent contact fields.
export const useSavedContacts = () => {
  const list = useSavedTravellers();
  return useMemo(() => {
    const seen = new Set();
    return list.filter((t) => t.email && !seen.has(t.email) && seen.add(t.email))
      .map((t) => ({ email: t.email, phone: t.phone || "", name: `${t.firstName} ${t.lastName}` }));
  }, [list]);
};

const age = (dob) => (dob ? Math.floor((Date.now() - new Date(dob)) / 3.15576e10) : null);

// First-name input that suggests saved customers; onPick receives the full saved traveller.
export const TravellerSuggest = ({ value, onChange, onPick, type, className, testId, placeholder = "First name" }) => {
  const saved = useSavedTravellers();
  const [open, setOpen] = useState(false);
  const box = useRef(null);
  const q = (value || "").trim().toLowerCase();
  const matches = saved.filter((t) => (!type || !t.type || t.type === type)
    && (!q || `${t.firstName} ${t.lastName}`.toLowerCase().includes(q))).slice(0, 8);

  useEffect(() => {
    const close = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  return (
    <div ref={box} className="relative">
      <input value={value} placeholder={placeholder} data-testid={testId} autoComplete="off" className={`w-full ${className || ""}`}
        onFocus={() => setOpen(true)} onChange={(e) => { onChange(e.target.value); setOpen(true); }} />
      {open && matches.length > 0 && (
        <div className="absolute left-0 top-full z-40 mt-1 max-h-64 w-72 overflow-y-auto rounded-xl border border-slate-200 bg-white py-1 shadow-xl" data-testid={`${testId}-suggestions`}>
          <p className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400">Saved customers</p>
          {matches.map((t) => (
            <button type="button" key={t.traveller_id} data-testid={`${testId}-suggestion-${t.traveller_id}`}
              onMouseDown={(e) => { e.preventDefault(); onPick(t); setOpen(false); }}
              className="flex w-full items-center gap-2.5 px-3 py-2 text-left hover:bg-[#F5F7FB]">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#1B1F5B]/10 text-[#1B1F5B]"><UserRound className="h-4 w-4" /></span>
              <span className="min-w-0">
                <span className="block truncate text-sm font-semibold text-slate-800">{t.title} {t.firstName} {t.lastName}</span>
                <span className="block truncate text-[11px] text-slate-500">
                  {[t.type, age(t.dob) !== null && `${age(t.dob)} yrs`, t.passport && `PP ${t.passport}`, t.phone].filter(Boolean).join(" · ")}
                </span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// <datalist> of saved customer emails / phones for contact inputs.
export const ContactDatalists = () => {
  const contacts = useSavedContacts();
  return (
    <>
      <datalist id="saved-emails">{contacts.map((c) => <option key={c.email} value={c.email}>{c.name}</option>)}</datalist>
      <datalist id="saved-phones">{contacts.filter((c) => c.phone).map((c) => <option key={c.email} value={c.phone}>{c.name}</option>)}</datalist>
    </>
  );
};

export const contactFor = (contacts, field, value) => contacts.find((c) => c[field] === value);
